import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Inbox, RefreshCw, Mail, Phone, Building2, User, Clock, FileText,
  CheckCircle2, XCircle, MessageSquare, Calendar, Trash2, ChevronRight,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const STATUS = [
  { key: 'all', label: 'Todas', color: 'bg-stone-100 text-stone-700' },
  { key: 'new', label: 'Nuevas', color: 'bg-orange-50 text-orange-700 border-orange-200' },
  { key: 'contacted', label: 'Contactadas', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  { key: 'scheduled', label: 'Programadas', color: 'bg-violet-50 text-violet-700 border-violet-200' },
  { key: 'converted', label: 'Convertidas', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  { key: 'discarded', label: 'Descartadas', color: 'bg-stone-100 text-stone-500' },
];

const STATUS_BADGE = {
  new: 'bg-orange-100 text-orange-800',
  contacted: 'bg-blue-100 text-blue-800',
  scheduled: 'bg-violet-100 text-violet-800',
  converted: 'bg-emerald-100 text-emerald-800',
  discarded: 'bg-stone-200 text-stone-600',
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' });
  } catch { return iso; }
};

const AdminDemoRequestsPage = () => {
  const [stats, setStats] = useState({ total: 0, pending: 0, contacted: 0, scheduled: 0, converted: 0 });
  const [requests, setRequests] = useState([]);
  const [status, setStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        axios.get(`${API}/api/admin/demo-requests/stats`, { headers: authHeaders() }),
        axios.get(`${API}/api/admin/demo-requests${status !== 'all' ? `?status=${status}` : ''}`, { headers: authHeaders() }),
      ]);
      setStats(s.data);
      setRequests(r.data.requests || []);
    } catch (err) {
      toast.error('Error al cargar solicitudes');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateStatus = async (id, newStatus, notesValue = null) => {
    try {
      await axios.patch(`${API}/api/admin/demo-requests/${id}`, {
        status: newStatus,
        notes: notesValue,
      }, { headers: authHeaders() });
      toast.success('Estado actualizado');
      await fetchData();
      if (selected && selected.id === id) {
        setSelected({ ...selected, status: newStatus, notes: notesValue ?? selected.notes });
      }
    } catch (err) {
      toast.error('No se pudo actualizar');
    }
  };

  const saveNotes = async () => {
    if (!selected) return;
    setSavingNotes(true);
    await updateStatus(selected.id, selected.status, notes);
    setSavingNotes(false);
  };

  const deleteRequest = async (id) => {
    if (!window.confirm('¿Eliminar esta solicitud definitivamente?')) return;
    try {
      await axios.delete(`${API}/api/admin/demo-requests/${id}`, { headers: authHeaders() });
      toast.success('Solicitud eliminada');
      setSelected(null);
      await fetchData();
    } catch (err) {
      toast.error('No se pudo eliminar');
    }
  };

  const openDetail = (req) => {
    setSelected(req);
    setNotes(req.notes || '');
  };

  return (
    <div className="space-y-6" data-testid="admin-demo-requests-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-stone-900 flex items-center gap-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#FF6B4A] to-[#FBBF24] flex items-center justify-center">
              <Inbox className="w-5 h-5 text-white" />
            </div>
            Solicitudes de Demo
          </h1>
          <p className="text-sm text-stone-500 mt-1">Bandeja de leads desde la landing page de Facturcal.</p>
        </div>
        <Button variant="outline" onClick={fetchData} disabled={loading} data-testid="refresh-demos">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Actualizar
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="Total" value={stats.total} icon={Inbox} color="text-stone-700" data-testid="stat-total" />
        <StatCard label="Nuevas" value={stats.pending} icon={Mail} color="text-orange-600" data-testid="stat-pending" />
        <StatCard label="Contactadas" value={stats.contacted} icon={Phone} color="text-blue-600" data-testid="stat-contacted" />
        <StatCard label="Programadas" value={stats.scheduled} icon={Calendar} color="text-violet-600" data-testid="stat-scheduled" />
        <StatCard label="Convertidas" value={stats.converted} icon={CheckCircle2} color="text-emerald-600" data-testid="stat-converted" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2" data-testid="status-filters">
        {STATUS.map((s) => (
          <button
            key={s.key}
            onClick={() => setStatus(s.key)}
            className={`px-4 py-2 text-sm font-semibold rounded-full transition-all border ${
              status === s.key
                ? 'bg-[#FF6B4A] text-white border-[#FF6B4A] shadow-md shadow-orange-200/50'
                : `${s.color} border-transparent hover:border-stone-300`
            }`}
            data-testid={`filter-${s.key}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        {/* List */}
        <div className="lg:col-span-3 bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-stone-400 text-sm">Cargando…</div>
          ) : requests.length === 0 ? (
            <div className="p-12 text-center" data-testid="empty-state">
              <Inbox className="w-10 h-10 mx-auto text-stone-300 mb-3" />
              <p className="text-stone-500">No hay solicitudes para este filtro.</p>
            </div>
          ) : (
            <ul className="divide-y divide-stone-100" data-testid="requests-list">
              {requests.map((r) => (
                <li key={r.id}>
                  <button
                    onClick={() => openDetail(r)}
                    className={`w-full text-left px-5 py-4 hover:bg-orange-50/40 transition-all flex items-center justify-between gap-3 ${selected?.id === r.id ? 'bg-orange-50/60' : ''}`}
                    data-testid={`demo-row-${r.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-stone-900 truncate">{r.name}</span>
                        {r.company && <span className="text-xs text-stone-400">· {r.company}</span>}
                        <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${STATUS_BADGE[r.status] || STATUS_BADGE.new}`}>{r.status}</span>
                      </div>
                      <div className="text-sm text-stone-500 truncate">{r.email}{r.interest ? ` · ${r.interest}` : ''}</div>
                      <div className="text-xs text-stone-400 mt-1 flex items-center gap-1.5"><Clock className="w-3 h-3" />{fmtDate(r.created_at)}</div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-stone-300 flex-shrink-0" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Detail */}
        <div className="lg:col-span-2">
          {selected ? (
            <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6 sticky top-6" data-testid="demo-detail">
              <div className="flex items-start justify-between mb-5">
                <div>
                  <h3 className="text-lg font-bold text-stone-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{selected.name}</h3>
                  {selected.company && <p className="text-sm text-stone-500 flex items-center gap-1.5 mt-0.5"><Building2 className="w-3.5 h-3.5" /> {selected.company}</p>}
                </div>
                <button onClick={() => deleteRequest(selected.id)} className="text-stone-400 hover:text-red-600 transition-colors" data-testid="delete-demo">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-2.5 text-sm border-t border-stone-100 pt-4">
                <Detail icon={Mail} label="Email"><a href={`mailto:${selected.email}`} className="text-[#FF6B4A] hover:underline">{selected.email}</a></Detail>
                {selected.phone && <Detail icon={Phone} label="Teléfono"><a href={`tel:${selected.phone}`} className="hover:underline">{selected.phone}</a></Detail>}
                {selected.employees && <Detail icon={User} label="Empleados">{selected.employees}</Detail>}
                {selected.sector && <Detail icon={Building2} label="Sector">{selected.sector}</Detail>}
                {selected.interest && <Detail icon={FileText} label="Interés">{selected.interest}</Detail>}
                <Detail icon={Clock} label="Recibida">{fmtDate(selected.created_at)}</Detail>
              </div>

              {selected.message && (
                <div className="mt-4 pt-4 border-t border-stone-100">
                  <p className="text-[11px] uppercase tracking-wider font-bold text-stone-400 mb-2 flex items-center gap-1.5"><MessageSquare className="w-3 h-3" />Mensaje</p>
                  <p className="text-sm text-stone-700 leading-relaxed bg-stone-50 rounded-xl p-3 whitespace-pre-wrap">{selected.message}</p>
                </div>
              )}

              {/* Actions */}
              <div className="mt-5 pt-5 border-t border-stone-100">
                <p className="text-[11px] uppercase tracking-wider font-bold text-stone-400 mb-2">Cambiar estado</p>
                <div className="grid grid-cols-2 gap-2 mb-4" data-testid="status-actions">
                  <ActionButton onClick={() => updateStatus(selected.id, 'contacted')} active={selected.status === 'contacted'} testid="set-contacted">
                    <Phone className="w-3.5 h-3.5" /> Contactada
                  </ActionButton>
                  <ActionButton onClick={() => updateStatus(selected.id, 'scheduled')} active={selected.status === 'scheduled'} testid="set-scheduled">
                    <Calendar className="w-3.5 h-3.5" /> Programada
                  </ActionButton>
                  <ActionButton onClick={() => updateStatus(selected.id, 'converted')} active={selected.status === 'converted'} testid="set-converted">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Convertida
                  </ActionButton>
                  <ActionButton onClick={() => updateStatus(selected.id, 'discarded')} active={selected.status === 'discarded'} testid="set-discarded">
                    <XCircle className="w-3.5 h-3.5" /> Descartar
                  </ActionButton>
                </div>

                <p className="text-[11px] uppercase tracking-wider font-bold text-stone-400 mb-2">Notas internas</p>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 text-sm bg-stone-50 border border-stone-200 rounded-xl focus:outline-none focus:border-[#FF6B4A] focus:bg-white resize-none"
                  placeholder="Anotaciones del contacto…"
                  data-testid="demo-notes"
                />
                <Button onClick={saveNotes} disabled={savingNotes} className="w-full mt-2 bg-[#FF6B4A] hover:bg-[#E05A3E]" data-testid="save-notes">
                  {savingNotes ? 'Guardando…' : 'Guardar notas'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="bg-stone-50/60 rounded-2xl border border-dashed border-stone-200 p-10 text-center">
              <Inbox className="w-8 h-8 mx-auto text-stone-300 mb-2" />
              <p className="text-sm text-stone-400">Selecciona una solicitud para ver el detalle.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, icon: Icon, color, ...rest }) => (
  <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-4" {...rest}>
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs uppercase tracking-wider font-bold text-stone-400">{label}</span>
      <Icon className={`w-4 h-4 ${color}`} />
    </div>
    <p className="text-2xl font-extrabold text-stone-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{value}</p>
  </div>
);

const Detail = ({ icon: Icon, label, children }) => (
  <div className="flex items-start gap-3">
    <Icon className="w-4 h-4 text-stone-400 mt-0.5 flex-shrink-0" />
    <div className="flex-1 min-w-0">
      <p className="text-[11px] uppercase tracking-wider font-bold text-stone-400">{label}</p>
      <p className="text-sm text-stone-800 break-words">{children}</p>
    </div>
  </div>
);

const ActionButton = ({ onClick, active, children, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border transition-all ${
      active
        ? 'bg-[#FF6B4A] text-white border-[#FF6B4A]'
        : 'bg-white border-stone-200 text-stone-700 hover:border-stone-300 hover:bg-stone-50'
    }`}
  >
    {children}
  </button>
);

export default AdminDemoRequestsPage;
